import { AnimatePresence, motion } from "framer-motion"
import { ChevronDown, Lightbulb } from "lucide-react"
import { type ReactNode, useState } from "react"
import { TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"

export interface HowItWorksItem {
	term: string
	text: ReactNode
}

function readOpen(storageKey: string): boolean {
	try {
		return localStorage.getItem(storageKey) !== "closed"
	} catch {
		return true
	}
}

/**
 * Короткое объяснение раздела простыми словами. Открыто при первом визите; свернул один
 * раз, и в этом браузере оно больше не мешает, но всегда под рукой.
 */
export function HowItWorks({
	id,
	title = "Как это работает",
	items,
	footer,
	className,
}: {
	/** Ключ, под которым браузер помнит, что блок свернули. */
	id: string
	title?: string
	items: HowItWorksItem[]
	footer?: ReactNode
	className?: string
}) {
	const storageKey = `kmk:how-it-works:${id}`
	const [open, setOpen] = useState(() => readOpen(storageKey))
	const toggle = () => {
		setOpen((value) => {
			try {
				localStorage.setItem(storageKey, value ? "closed" : "open")
			} catch {
				// Хранилище недоступно: блок просто откроется снова в следующий раз.
			}
			return !value
		})
	}

	return (
		<section className={cn("rounded-2xl border border-border bg-card/60", className)}>
			<button
				type="button"
				aria-expanded={open}
				onClick={toggle}
				className="flex w-full items-center gap-3 px-4 py-3 text-left sm:px-5"
			>
				<span className="grid size-8 shrink-0 place-items-center rounded-lg bg-amber-400/15 text-amber-300">
					<Lightbulb className="size-4" />
				</span>
				<span className="flex-1 text-sm font-medium">{title}</span>
				<ChevronDown
					className={cn(
						"size-4 text-muted-foreground transition-transform duration-200",
						open && "rotate-180",
					)}
				/>
			</button>
			<AnimatePresence initial={false}>
				{open ? (
					<motion.div
						initial={{ opacity: 0, height: 0 }}
						animate={{ opacity: 1, height: "auto" }}
						exit={{ opacity: 0, height: 0 }}
						transition={TRANSITION.base}
						className="overflow-hidden"
					>
						<dl className="grid gap-x-8 gap-y-4 px-4 pt-1 pb-5 sm:grid-cols-2 sm:px-5">
							{items.map((item) => (
								<div key={item.term}>
									<dt className="text-sm font-medium">{item.term}</dt>
									<dd className="mt-1 text-sm leading-relaxed text-muted-foreground">{item.text}</dd>
								</div>
							))}
						</dl>
						{footer ? (
							<div className="border-t border-border px-4 py-3 text-sm text-muted-foreground sm:px-5">
								{footer}
							</div>
						) : null}
					</motion.div>
				) : null}
			</AnimatePresence>
		</section>
	)
}
